/* eslint-disable no-debugger */
/* eslint-disable react/jsx-no-useless-fragment */
// (c) Copyright 2022 HP Development Company, L.P.
import React, { useEffect, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import ConnectionStatus from './components/ConnectionStatus'

import { useKvmStatusStore } from './hooks/KvmStatus'

import { signalClient, connectionClient, toggleFullscreenMode } from './utils'
import { WebRTCComponent, useHyperspace } from '@acs-manageability/webrtc-react'
import { WebRTCToolbar } from './components/WebRTCToolbar'
import { useConnection } from './contexts/ConnectionProvider'
import { addListener, removeListener } from './utils/EventUtils'
import { ConnectStepErrorMapping } from './constants/Options'

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  position: relative;

  & .toolbar .telemetry {
    padding: 0;
  }
`

let videoPlayStateTask = null
let lastTaskTime = 0

const postMessageToParent = (type, props) => {
  if (window.parent && window.parent !== window && window.parent.window) {
    window.parent.window.postMessage(
      {
        type: type,
        ...(props || {}),
      },
      '*',
    )
    return true
  }
  return false
}

function RemoteAccessPage() {
  const { connect, setIsMuted } = useConnection()
  const [connectStep, errorType, isInited, connected] = useKvmStatusStore((set) => [
    set.connectStep,
    set.errorType,
    set.isInited,
    set.connected,
  ])
  const [setConnectStatusByPeerConnection, setConnected, setMessageCode, setErrorType] =
    useKvmStatusStore((set) => [
      set.setConnectStatusByPeerConnection,
      set.setConnected,
      set.setMessageCode,
      set.setErrorType,
    ])
  const { getVideoRef } = useHyperspace()
  const ref = getVideoRef()

  const sendMessage = useCallback((message) => {
    return signalClient.sendMessage(message)
  }, [])

  useEffect(() => {
    const video = ref?.current
    const checkConnectionState = () => {
      clearTimeout(videoPlayStateTask)
      if (connectStep === 'establish_stream') {
        if (lastTaskTime === 0) {
          lastTaskTime = Date.now()
        }

        if (video) {
          const readyState = video.readyState
          console.log('video.readyState', readyState)
          if (readyState === 4) {
            setConnected()
          } else {
            if (Date.now() - lastTaskTime > 20000) {
              setErrorType('no_stream')
            } else {
              videoPlayStateTask = setTimeout(checkConnectionState, 3000)
            }
          }
        }
      }
    }
    checkConnectionState()
  }, [ref, connectStep, setConnected, setErrorType])

  useEffect(() => {
    const flowErrors = Object.values(ConnectStepErrorMapping)
    if (flowErrors.includes(errorType)) {
      postMessageToParent('clean-session')
    }
  }, [errorType])

  const windowCloseHandler = useCallback(() => {
    if (!postMessageToParent('close')) {
      connectionClient.disconnectFromServer()
      window.close()
    }
  }, [])

  const windowReloadHandler = useCallback(() => {
    if (!postMessageToParent('reload')) {
      connectionClient.disconnectFromServer()
      window.location.reload()
    }
  }, [])

  const handleFullscreenToggleRequest = useCallback((isFull) => {
    if (postMessageToParent('screen-change', { isFull })) {
      return true
    }

    return toggleFullscreenMode(isFull)
  }, [])

  useEffect(() => {
    if (isInited) {
      if (ref && ref.current) {
        setIsMuted(true)
        // ref.current.style.cursor = "none";
        ref.current.muted = true
        ref.current.onloadedmetadata = () => {
          ref.current.play()
        }

        // const fullScreenBtn = document.querySelector('button[data-testid="Fullscreen mode"]');
        // if (fullScreenBtn) {
        // 	fullScreenBtn.remove();
        // }

        const mainContent = document.querySelector('#canvas')
        if (mainContent) {
          mainContent.style.margin = '0 auto'
        }
      }
    }
  }, [isInited, ref, setIsMuted])

  useEffect(() => {
    const handleConnection = ({ sessionToken, deviceId }) => {
      connect(sessionToken, deviceId)
    }
    addListener('session-token', handleConnection)

    window.parent.window.postMessage(
      {
        type: 'init-session',
      },
      '*',
    )
    return () => {
      removeListener('session-token', handleConnection)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onDataChannel = useCallback(
    (message) => {
      const { message_type: type, content = { message_code: 0 } } = message
      if (type === 'state') {
        setMessageCode(content?.message_code || 0)
      }
    },
    [setMessageCode],
  )

  const View = useMemo(() => {
    console.log('WebRtc View rerender')
    if (!isInited) {
      return null
    }
    return (
      <>
        <WebRTCToolbar
          renderCtrlAltDelButton
          renderPointerLockButton
          renderLatency
          onFullscreenToggleRequest={handleFullscreenToggleRequest}
        />
        <WebRTCComponent
          sendMessageCallback={sendMessage}
          onDisconnect={windowCloseHandler}
          onIceConnectionStateChange={(state) => {
            console.log('Ice connection state:', state)
          }}
          onConnectionStateChange={(state) => {
            console.log('Connection state:', state)
            setConnectStatusByPeerConnection(state)
          }}
          onMessage={onDataChannel}
          renderLatency
          onError={(e) => console.log(e)}
        />
      </>
    )
  }, [
    handleFullscreenToggleRequest,
    isInited,
    onDataChannel,
    sendMessage,
    setConnectStatusByPeerConnection,
    windowCloseHandler,
  ])

  console.log('client - agent connected', connected)

  return (
    <Container>
      {View}
      {!connected && (
        <ConnectionStatus
          error={errorType}
          step={connectStep}
          onRetry={windowReloadHandler}
          onClose={windowCloseHandler}
          onAction={(event) => {
            const dom = ref?.current
            if (dom) {
              const rect2 = dom.getBoundingClientRect()
              const x = event.clientX - rect2.left
              const y = event.clientY - rect2.top

              const newEvent = new MouseEvent('mousemove', {
                bubbles: true,
                cancelable: true,
                clientX: x + rect2.left,
                clientY: y + rect2.top,
              })

              dom.dispatchEvent(newEvent)
            }
          }}
        />
      )}
    </Container>
  )
}

export default RemoteAccessPage
